import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Sparkles } from "lucide-react";
import { API } from "../App";
import { renderMarkdownSafe } from "../lib/safeMarkdown";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export default function WhyChooseUsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/why-choose-us`)
      .then((r) => alive && setItems(r.data || []))
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/5 p-6">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold logo-text">
            TAXINEARME
          </Link>
          <Link
            to="/"
            className="text-white/60 hover:text-[#C5A059] flex items-center gap-2 text-sm"
            data-testid="wcu-back-home"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto p-6 lg:p-12" data-testid="wcu-page">
        <div className="mb-10">
          <div className="flex items-center gap-3 text-[#C5A059] mb-3">
            <Sparkles className="w-6 h-6" />
            <span className="text-xs uppercase tracking-widest">
              Why book with us
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold">
            Why Choose Us
          </h1>
          <p className="text-white/50 mt-4 text-base">
            What sets our transfer service apart — straight from our promise to
            you.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner" />
          </div>
        ) : items.length === 0 ? (
          <div
            className="bg-[#0F0F0F] border border-white/5 rounded-sm p-8 text-center text-white/50"
            data-testid="wcu-empty"
          >
            <p>Content coming soon.</p>
          </div>
        ) : (
          <Accordion
            type="single"
            collapsible
            className="space-y-2"
            data-testid="wcu-accordion"
          >
            {items.map((item) => (
              <WCUItem key={item.item_id} item={item} />
            ))}
          </Accordion>
        )}
      </main>
    </div>
  );
}

function WCUItem({ item }) {
  const html = useMemo(
    () => ({ __html: renderMarkdownSafe(item.content, "wcu-public") }),
    [item.content]
  );
  return (
    <AccordionItem
      value={item.item_id}
      className="bg-[#0F0F0F] border border-white/5 rounded-sm px-5 !border-b"
      data-testid={`wcu-item-${item.item_id}`}
    >
      <AccordionTrigger className="text-white hover:no-underline text-left text-base font-medium py-5">
        {item.question}
      </AccordionTrigger>
      <AccordionContent className="text-white/70">
        <article
          className="prose-custom prose-sm"
          dangerouslySetInnerHTML={html}
        />
      </AccordionContent>
    </AccordionItem>
  );
}
